import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Dialog, IconButton, Portal, Stack, useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.5;
const DETAIL_SIZE = 520;
const DETAIL_ZOOM = 3.8;
const LENS_RATIO = 1 / DETAIL_ZOOM;

type Props = {
  src: string;
  alt?: string;
  outgoing?: boolean;
};

type HoverState = {
  ratioX: number;
  ratioY: number;
  thumbRect: DOMRect;
};

/**
 * Mobile / tactile : clic ? plein �cran, +/- et d�placement au doigt.
 * Desktop : survol ? fen�tre lat�rale agrandie montrant le d�tail sous le curseur.
 */
export default function ZoomableChatImage({ src, alt = 'Image', outgoing }: Props) {
  const canHoverZoom = useMediaQuery('(hover: hover) and (pointer: fine)');
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [hover, setHover] = useState<HoverState | null>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const resetViewer = useCallback(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setDragging(false);
    dragRef.current = null;
  }, []);

  useEffect(() => {
    if (!fullscreenOpen) resetViewer();
  }, [fullscreenOpen, resetViewer]);

  useLayoutEffect(() => {
    if (!hover) {
      setPanelPos(null);
      return;
    }
    const gap = 16;
    const margin = 12;
    const { thumbRect } = hover;
    let left = outgoing
      ? thumbRect.left - DETAIL_SIZE - gap
      : thumbRect.right + gap;
    let top = thumbRect.top + thumbRect.height / 2 - DETAIL_SIZE / 2;

    if (left < margin) {
      left = thumbRect.right + gap;
    }
    if (left + DETAIL_SIZE > window.innerWidth - margin) {
      left = thumbRect.left - DETAIL_SIZE - gap;
    }
    if (left < margin) {
      left = Math.max(margin, (window.innerWidth - DETAIL_SIZE) / 2);
    }
    top = Math.max(margin, Math.min(top, window.innerHeight - DETAIL_SIZE - margin));
    setPanelPos({ top, left });
  }, [hover, outgoing]);

  const clampOffset = useCallback((next: { x: number; y: number }, nextZoom: number) => {
    const el = viewportRef.current;
    if (!el || nextZoom <= 1) return { x: 0, y: 0 };
    const maxX = (el.clientWidth * (nextZoom - 1)) / 2;
    const maxY = (el.clientHeight * (nextZoom - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, next.x)),
      y: Math.max(-maxY, Math.min(maxY, next.y)),
    };
  }, []);

  const setZoomClamped = (value: number) => {
    const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(value * 10) / 10));
    setZoom(next);
    setOffset((prev) => clampOffset(prev, next));
  };

  const updateHoverFromEvent = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!canHoverZoom) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratioX = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const ratioY = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
    setHover({
      ratioX,
      ratioY,
      thumbRect: rect,
    });
  };

  const onThumbClick = () => {
    if (canHoverZoom) return;
    setFullscreenOpen(true);
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (zoom <= 1) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: offset.x,
      originY: offset.y,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const next = {
      x: drag.originX + (event.clientX - drag.startX),
      y: drag.originY + (event.clientY - drag.startY),
    };
    setOffset(clampOffset(next, zoom));
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    }
    dragRef.current = null;
    setDragging(false);
  };

  const lensW = hover ? hover.thumbRect.width * LENS_RATIO : 0;
  const lensH = hover ? hover.thumbRect.height * LENS_RATIO : 0;
  const lensLeft = hover
    ? Math.min(
        Math.max(0, hover.ratioX * hover.thumbRect.width - lensW / 2),
        hover.thumbRect.width - lensW
      )
    : 0;
  const lensTop = hover
    ? Math.min(
        Math.max(0, hover.ratioY * hover.thumbRect.height - lensH / 2),
        hover.thumbRect.height - lensH
      )
    : 0;

  // Position du d�tail : centr� sur la zone loupe (ratio de la lentille, pas du curseur brut)
  const detailRatioX =
    hover && hover.thumbRect.width > 0
      ? (lensLeft + lensW / 2) / hover.thumbRect.width
      : 0.5;
  const detailRatioY =
    hover && hover.thumbRect.height > 0
      ? (lensTop + lensH / 2) / hover.thumbRect.height
      : 0.5;

  return (
    <>
      <Box
        onClick={onThumbClick}
        onMouseEnter={updateHoverFromEvent}
        onMouseMove={updateHoverFromEvent}
        onMouseLeave={() => setHover(null)}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 1,
          maxWidth: '100%',
          maxHeight: 240,
          cursor: canHoverZoom ? 'crosshair' : 'pointer',
          bgcolor: outgoing ? 'rgba(255,255,255,0.12)' : 'action.hover',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          draggable={false}
          sx={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: 240,
            width: 'auto',
            height: 'auto',
            objectFit: 'cover',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
        {canHoverZoom && hover ? (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: lensLeft,
              top: lensTop,
              width: lensW,
              height: lensH,
              border: '2px solid rgba(255,255,255,0.95)',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.35), inset 0 0 0 9999px rgba(0,0,0,0.12)',
              pointerEvents: 'none',
              borderRadius: 0.5,
            }}
          />
        ) : null}
      </Box>

      {canHoverZoom && hover && panelPos ? (
        <Portal>
          <Box
            role="img"
            aria-label={`D�tail : ${alt}`}
            sx={{
              position: 'fixed',
              top: panelPos.top,
              left: panelPos.left,
              width: DETAIL_SIZE,
              height: DETAIL_SIZE,
              zIndex: 1400,
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#111',
              boxShadow: '0 12px 40px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.12)',
              pointerEvents: 'none',
            }}
          >
            <Box
              component="img"
              src={src}
              alt=""
              draggable={false}
              sx={{
                position: 'absolute',
                width: hover.thumbRect.width * DETAIL_ZOOM,
                height: hover.thumbRect.height * DETAIL_ZOOM,
                maxWidth: 'none',
                objectFit: 'cover',
                left: -(detailRatioX * hover.thumbRect.width * DETAIL_ZOOM - DETAIL_SIZE / 2),
                top: -(detailRatioY * hover.thumbRect.height * DETAIL_ZOOM - DETAIL_SIZE / 2),
                userSelect: 'none',
              }}
            />
          </Box>
        </Portal>
      ) : null}

      <Dialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.96)',
            m: 0,
            borderRadius: 0,
          },
        }}
      >
        <IconButton
          onClick={() => setFullscreenOpen(false)}
          aria-label="Fermer"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 2,
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.14)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: 'absolute',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            bgcolor: 'rgba(0,0,0,0.45)',
            borderRadius: 999,
            px: 1,
            py: 0.5,
          }}
        >
          <IconButton
            aria-label="Dézoomer"
            onClick={() => setZoomClamped(zoom - ZOOM_STEP)}
            disabled={zoom <= ZOOM_MIN}
            sx={{ color: '#fff' }}
          >
            <RemoveIcon />
          </IconButton>
          <IconButton
            aria-label="Zoomer"
            onClick={() => setZoomClamped(zoom + ZOOM_STEP)}
            disabled={zoom >= ZOOM_MAX}
            sx={{ color: '#fff' }}
          >
            <AddIcon />
          </IconButton>
        </Stack>

        <Box
          ref={viewportRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            touchAction: zoom > 1 ? 'none' : 'pan-y',
            cursor: zoom > 1 ? 'grab' : 'default',
            '&:active': { cursor: zoom > 1 ? 'grabbing' : 'default' },
          }}
        >
          <Box
            component="img"
            src={src}
            alt={alt}
            draggable={false}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              userSelect: 'none',
              pointerEvents: 'none',
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
              transition: dragging ? 'none' : 'transform 120ms ease-out',
              transformOrigin: 'center center',
            }}
          />
        </Box>
      </Dialog>
    </>
  );
}
